class BundleButton extends HTMLElement {
    #variantIdsToQuantity = {};
    #discountCode = '';
    #lineItemProperty = '';
    #textContent = '';
    #atcButton = null;

    static get observedAttributes() {
        return ['variants', 'discount-code', 'text'];
    }

    connectedCallback() {
        this.#initialize();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue) {
            this.#initialize();
        }
    }

    #initialize() {
        const { variants, discountCode, text, lineItemProperty } = this.#retrieveAttributes();
        
        if (!variants || !discountCode) {
            console.warn('BundleButton: Missing required attributes');
            return;
        }

        this.#discountCode = discountCode;
        this.#textContent = text || window.variantStrings.addToCart;
        this.#lineItemProperty = lineItemProperty || null;
        this.#processVariants(variants);
        this.#injectButtonHTML();
    }

    #retrieveAttributes() {
        return {
            variants: this.getAttribute('variants'),
            discountCode: this.getAttribute('discount-code'),
            lineItemProperty: this.getAttribute('line-item-property'),
            text: this.getAttribute('text') || window.variantStrings.addToCart
        };
    }

    #injectButtonHTML() {
        this.innerHTML = `
            <button class="atc-button button button--primary">
                ${this.#textContent}
            </button>
        `;
        this.#atcButton = this.querySelector('.atc-button');
        this.#atcButton.addEventListener('click', this.#handleClick.bind(this));
    }

    #processVariants(variantsRaw) {
        this.#variantIdsToQuantity = variantsRaw.split(':').reduce((acc, variantString) => {
            const [variantId, quantity = '1'] = variantString.split('-');
            acc[variantId] = parseInt(quantity, 10);
            return acc;
        }, {});
    }

    async #handleClick() {
        this.#setButtonState('adding');
        try {
            await this.#processAtc();
            await this.#addDiscountCodeAfterCart();
            this.#setButtonState('added');
        } catch (error) {
            console.error('Error:', error);
            this.#setButtonState('error');
            window.setTimeout(() => {
                this.#setButtonState('added');
            }, 3000)
        }
    }

    #setButtonState(state) {
        const states = {
            adding: { text: window.cartStrings.itemsAdded, disabled: true, class: 'button--primary' },
            added: { text: this.#textContent, disabled: false, class: 'button--primary' },
            error: { text: window.cartStrings.addError, disabled: true, class: 'button--secondary button--secondary--red' }
        };

        const { text, disabled, class: className } = states[state];
        this.#atcButton.innerText = text;
        this.#atcButton.disabled = disabled;
        this.#atcButton.className = `atc-button button ${className}`;
    }

    async #processAtc() {
        const formData = {
            items: Object.entries(this.#variantIdsToQuantity).map(([id, quantity]) => ({
                id,
                quantity,
                properties: {'_Bundle': this.#lineItemProperty},
                discounts: [this.#discountCode]
            }))
        };

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(formData)
        });

        if (!response.ok) throw new Error('Failed to add items to cart');
        return response.json();
    }

    async #addDiscountCodeAfterCart() {
        if (!this.#discountCode) return;

        await fetch(`/discount/${this.#discountCode}`);
        const cartUpdate = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates: {} })
        });

        if (!cartUpdate.ok) throw new Error('Failed to update cart');
        const cartUpdateResponse = await cartUpdate.json();

        setTimeout(() => this.#updateDisplayAndOpenCart(cartUpdateResponse), 500);
    }

    #updateDisplayAndOpenCart(res) {
        if (typeof cartDrawer === "undefined") return;

        cartDrawer.updateCartDisplay(res);
        cartDrawer.open(window.accessibilityStrings.cartItemAdded);
    }
}

customElements.define("bundle-button", BundleButton);

class BundleButtonSelector extends HTMLElement {
    #discountCode = '';
    #textContent = '';
    #placeholder = '';
    #defaultButtonText = '';
    #optionToVariantSets = {}
    #variants = '';

    connectedCallback() {
        this.#initialize();
    }

    #initialize() {
        const { variants, discountCode, text, placeholder, defaultButtonText } = this.#retrieveAttributes();
        
        if (!variants || !discountCode || !placeholder) {
            console.warn('BundleButton: Missing required attributes');
            return;
        }

        this.#discountCode = discountCode;
        this.#textContent = text;
        this.#defaultButtonText = defaultButtonText;
        this.#placeholder = placeholder;
        this.#processVariants(variants);
        this.#injectSelectorHTML();
    }

    #processVariants(variantsRaw) {
        const variantsOptionSets = variantsRaw.split('||')

        for (const variantOptionSet of variantsOptionSets) {
            const splitVariantOptionSet = variantOptionSet.split('|')
            const variantOptionTitle = splitVariantOptionSet[0]
            const variantsSingle = splitVariantOptionSet[1]

            this.#optionToVariantSets[variantOptionTitle] = variantsSingle
        }
    }

    #injectSelectorHTML() {
        let selectorString = `<select name="Bundle Selector">`

        if (this.#placeholder) {
            selectorString += `<option value="" disabled selected>${this.#placeholder}</option>`
        }

        for (const key of Object.keys(this.#optionToVariantSets)) {
            selectorString += `<option value="${key}">${key}</option>`
        }

        selectorString += '</select>'

        const defaultButtonTextContent = this.#defaultButtonText || this.#textContent || window.variantStrings.addToCart

        selectorString += `<button class="hide-only-button button button--primary" disabled="disabled">${defaultButtonTextContent}</button>`

        for (const key of Object.keys(this.#optionToVariantSets)) {
            selectorString += `<bundle-button
                class="hidden"
                option-value="${key}"
                variants="${this.#optionToVariantSets[key]}"
                discount-code="${this.#discountCode}"
                text="${this.#textContent || window.variantStrings.addToCart}"
            ></bundle-button>`
        }

        this.innerHTML = selectorString
        this.#attachSelectListener()
    }

    #attachSelectListener() {
        const hiddenButtonClass = 'hidden'
        const selector = this.querySelector('select')
        const allButtons = this.querySelectorAll('bundle-button')
        const defaultButton = this.querySelector('.hide-only-button')

        selector.addEventListener('change', (e) => {
            const selectedButton = this.querySelector(`bundle-button[option-value="${e.target.value}"]`)

            for (const button of allButtons) {
                button.classList.add(hiddenButtonClass)
            }

            defaultButton.classList.add(hiddenButtonClass)
            selectedButton.classList.remove(hiddenButtonClass)
        })
    }

    #retrieveAttributes() {
        return {
            variants: this.getAttribute('variants'),
            discountCode: this.getAttribute('discount-code'),
            text: this.getAttribute('text'),
            placeholder: this.getAttribute('placeholder'),
            defaultButtonText: this.getAttribute('default-button-text')
        };
    }
}

customElements.define("bundle-button-selector", BundleButtonSelector);
